import { FC, useState, useMemo, useEffect } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useQuery, useMutation } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { observer } from 'mobx-react-lite';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import IconButton from '@material-ui/core/IconButton';
import PersonIcon from '@material-ui/icons/Person';
import SyncIcon from '@mui/icons-material/Sync';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import { useStyles } from './styles';
import {
  Paper,
  Tooltip,
  Snackbar,
  SnackbarContent,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@material-ui/core';
import EmailIcon from '../../../assets/icons/EndUser/Email.svg';
import { green, red } from '@material-ui/core/colors';
import { getRowsPerPageOptions } from 'utils/pagination';
import { Search } from 'components/Search';

export const MicrosoftSyncUser: FC = observer(() => {
  const classes = useStyles();
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // const [dialogData, setDialogData] = useState<any[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const {
    MicrosoftStore: {
      microsoftsyncUser,
      allsyncUsersView,
      microsoftreSyncSingleUser,
      microsoftreSyncBulkUser,
      microsoftreSyncUser,
    },
    dashboardStore: { generalStatistic, getStatistic },
    ldapStore: { usersView, getUsers },
    notificationStore: { openNotification },
  } = useStore();
  const microsoftsyncUserReq = useQuery('syncUser', () => microsoftsyncUser(), {
    onError: () => openNotification('error', `We are unable to get the user from Microsoft`),
  });
  const getUsersReq = useQuery('getUsersReq', () => getUsers(), {
    onError: () => openNotification('error', `Can't get users`),
  });
  const getStatisticReq = useQuery('getStatisticReq', () => getStatistic(), {
    onError: () => openNotification('error', `Can't get statistic`),
  });
  const microsoftreSyncUserReq = useMutation(
    () =>
      microsoftreSyncUser({
        sync_users: true,
        sync_groups: false,
        microsoft_to_ldap: true,
        ldap_to_microsoft: true,
      }),
    {
      mutationKey: 'microsoftreSyncUserReq',
      onSuccess: () => {
        setOpen(false);
        openNotification('success', 'Bi-direction Sync Successful');
        microsoftsyncUserReq.refetch();
        getUsersReq.refetch();
      },
      onError: ({ message }: Error) => {
        setOpen(false);
        openNotification('error', message);
      },
    }
  );
  const microsoftreSyncSingleUserReq = useMutation((data: { email: string }) => microsoftreSyncSingleUser(data), {
    mutationKey: 'microsoftreSyncSingleUser',
    onSuccess: (_, variables) => {
      openNotification('success', `Selected Users sync'ed with App Microsoft Successfully`);
      microsoftsyncUserReq.refetch();
      getUsersReq.refetch();
    },
    onError: (error: any) => {
      setOpen(false);
      openNotification('error', `We are unable to sync the group from zoom`);
    },
  });

  const microsoftreSyncBulkUserReq = useMutation((data: any) => microsoftreSyncBulkUser(data), {
    mutationKey: 'microsoftreSyncBulkUser',
    onSuccess: () => {
      openNotification('success', 'Selected Users synced with Microsoft Successfully');
      setSelectedUsers([]);
      microsoftsyncUserReq.refetch();
      getUsersReq.refetch();
    },
    onError: (error: any) => {
      openNotification('error', 'Unable to sync selected users');
    },
  });

  useEffect(() => {
    document.title = 'EasyIdentity: App: SyncUser';
  }, []);

  const user = usersView.map((item: { microsoftId: any }) => item.microsoftId);
  const filteredUserSet = new Set(user);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const syncedUsers = new Set();
  const rows = useMemo(() => {
    let filteredData = !microsoftsyncUserReq.isFetching ? allsyncUsersView : [];

    if (searchFilter && searchFilter.trim() !== '') {
      const searchTerm = searchFilter.toLowerCase().trim();
      filteredData = filteredData.filter((user: any) => {
        const searchableText = `${user.displayName || ''} ${user.userPrincipalName || ''}`.toLowerCase();
        return searchableText.includes(searchTerm);
      });
    }

    return filteredData;
  }, [microsoftsyncUserReq.isFetching, allsyncUsersView, searchFilter]);
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const handleSync = (email: string) => {
    microsoftreSyncSingleUserReq.mutate({ email });
  };
  const handleBulkSync = () => {
    microsoftreSyncBulkUserReq.mutate({ emails: selectedUsers });
  };

  const handleSyncAll = () => {
    // Show dialog with data first
    // setDialogData(allsyncUsersView);
    setDialogOpen(true);
  };

  const confirmSyncAll = () => {
    setDialogOpen(false);
    setLoadingMessage('Sync is in Progress. Please be patient, it may take few minutes.');
    microsoftreSyncUserReq.mutate();
    setOpen(true);
  };

  const handleSelectUser = (email: string, checked: boolean) => {
    if (checked) {
      setSelectedUsers((prev) => [...prev, email]);
    } else {
      setSelectedUsers((prev) => prev.filter((e) => e !== email));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allEmails = allsyncUsersView.map((user) => user.userPrincipalName).filter(Boolean);
      setSelectedUsers(allEmails);
    } else {
      setSelectedUsers([]);
    }
  };
  const columns: GridColumns = [
    {
      field: 'select',
      headerName: '',
      width: 50,
      renderHeader: () => (
        <Checkbox
          checked={selectedUsers.length === allsyncUsersView.length && allsyncUsersView.length > 0}
          indeterminate={selectedUsers.length > 0 && selectedUsers.length < allsyncUsersView.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      renderCell: (params: GridCellParams) => (
        <Checkbox
          checked={selectedUsers.includes(params.row.userPrincipalName)}
          onChange={(e) => handleSelectUser(params.row.userPrincipalName, e.target.checked)}
        />
      ),
    },
    {
      field: 'icon',
      headerName: ' ',
      width: 40,
      align: 'center',
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => <PersonIcon style={{ color: 'grey' }} />,
    },
    {
      field: 'displayName',
      headerName: 'FULL NAME',
      flex: 0.7,
      renderCell: (params: GridCellParams) => (
        <Tooltip title={params.row.displayName || ''}>
          <div>
            <span>{params.row.displayName}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'userPrincipalName',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <Tooltip title={value || ''} placement='bottom'>
            <div style={{ display: 'flex', gap: '10px' }}>
              <img src={EmailIcon} alt='' />
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'ACTIONS',
      width: 160,
      disableColumnMenu: true,
      renderCell: (params: GridCellParams) => (
        <>
          {!filteredUserSet.has(params.row.microsoftId) && !syncedUsers.has(params.row.microsoftId) ? (
            <Tooltip title='Not Sync'>
              <IconButton size='small' onClick={() => handleSync(params.row.userPrincipalName)}>
                <SyncDisabledIcon style={{ color: red[500] }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title='Sync'>
              <IconButton size='small' onClick={() => handleSync(params.row.userPrincipalName)}>
                <SyncIcon style={{ color: green[500] }} />
              </IconButton>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  const ldapUserCount = usersView.length ?? 0;
  const microsoftUserCount = allsyncUsersView.length ?? 0;
  const disabledSyncCount = rows
    .filter((row) => row.microsoftId !== null)
    .filter(
      (row) => row.microsoftId && !filteredUserSet.has(row.microsoftId) && !syncedUsers.has(row.microsoftId)
    ).length;

  useAppPreloader(
    microsoftsyncUserReq.isFetching ||
      microsoftreSyncUserReq.isLoading ||
      microsoftreSyncSingleUserReq.isLoading ||
      microsoftreSyncBulkUserReq.isLoading ||
      getUsersReq.isFetching ||
      getStatisticReq.isFetching
  );
  return (
    <>
      {!microsoftsyncUserReq.isFetching && !getUsersReq.isFetching && !getStatisticReq.isFetching && (
        <Paper className={classes.dataGrid} elevation={0}>
          <>
            <h1 className={classes.textBox}>You have {ldapUserCount} Users in EasyIdentity.</h1>
            <h1 className={classes.textBox}>
              You have {microsoftUserCount} Users in Microsoft Entra / Office 365 Workspace
            </h1>
            <h1 className={classes.textBox}>
              Sync Users : EasyIdentity {'<->'} Microsoft Entra / Office 365 Workspace
              <SyncIcon style={{ color: green[500], cursor: 'pointer' }} onClick={handleSyncAll} />
            </h1>
            {selectedUsers.length > 0 && (
              <Button variant='contained' color='primary' onClick={handleBulkSync} style={{ margin: '10px 0' }}>
                Sync Selected Users ({selectedUsers.length})
              </Button>
            )}
            {disabledSyncCount > Number(generalStatistic?.leftAccounts) && (
              <h1 className={classes.textBox}>
                There are only {generalStatistic?.leftAccounts || '0'} Easyidentity User Licenses remaining. Please
                upgrade your subscription to Sync Easyidentity User Accounts with Microsoft Entra / Office 365
                Workspace.
              </h1>
            )}
          </>
        </Paper>
      )}
      <Paper className={classes.dataGrid} elevation={0}>
        <>
          <h1 style={{ fontWeight: '400', fontSize: '120%', textAlign: 'center' }}>
            List of All Microsoft Entra / Office 365 Workspace Users
          </h1>
        </>
      </Paper>
      <Paper className={classes.dataGrid} elevation={0}>
        <div style={{ border: '1px solid #ccc' }}>
          <Search handelSearch={setSearchFilter} />
        </div>
        {/* <Search handelSearch={setSearchFilter}  /> */}
      </Paper>

      <Paper className={classes.dataGrid} elevation={0}>
        <DataGrid
          className={classes.noBorder}
          autoHeight
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          rowsPerPageOptions={rowsPerPageOptions}
          pagination
          onPageChange={(params) => setPageSize(params.pageSize)}
        />
      </Paper>
      <Snackbar open={open}>
        <SnackbarContent style={{ backgroundColor: green[500] }} message={loadingMessage} />
      </Snackbar>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth='md'>
        <DialogTitle>Sync All Users Confirmation</DialogTitle>
        <DialogContent>
          <Typography variant='body1' gutterBottom>
            Do you want to sync all users?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color='secondary'>
            Cancel
          </Button>
          <Button onClick={confirmSyncAll} color='primary' variant='contained'>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
